/**
 * AWS API Client
 * Generic layer for AWS API operations with automatic authorization headers
 */

import { AuthClientStore, getANPHost } from "@repo/core";
import { get, post, patch, put, del } from "aws-amplify/api";

export interface ApiRequestConfig {
  headers?: Record<string, string>;
  queryStringParameters?: Record<string, unknown>;
  body?: unknown;
}

/**
 * Error interface for API errors
 */
export interface ApiError {
  message: string;
  status?: number;
  statusText?: string;
  roboMarketsStatus?: number;
  isServiceOverloaded?: boolean;
  [key: string]: unknown; // Allow additional error properties
}

/**
 * AWS API Client with automatic authorization
 */
export default class AwsApiClient {
  /**
   * The API name used for all requests
   */
  private static readonly API_NAME = "apiIndicatorApp";

  /**
   * The API path prefix for all requests
   */
  private static readonly API_PATH_PREFIX = "/api/v1";

  /**
   * Build the full path with prefix
   */
  private static buildPath(path: string): string {
    // Ensure path starts with /
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${this.API_PATH_PREFIX}${normalizedPath}`;
  }

  /**
   * Merge headers with authorization token
   * Only includes Authorization header if a valid token exists
   */
  private static mergeHeaders(
    customHeaders?: Record<string, string>
  ): Record<string, string> {
    const accessToken = AuthClientStore.getAccessToken();
    const headers: Record<string, string> = {
      ...customHeaders,
    };

    // Only add Authorization header if we have a valid token
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    return headers;
  }

  /**
   * Refresh the access token using the refresh token
   */
  private static async refreshAccessToken(): Promise<boolean> {
    const refreshToken = AuthClientStore.getRefreshToken();

    if (!refreshToken) {
      console.log("No refresh token available for refresh");
      return false;
    }

    try {
      console.log("Attempting to refresh access token...");
      // Use the same pattern as the regular API client
      const baseURL = getANPHost();
      const response = await fetch(`${baseURL}/api/token/refresh/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.access) {
          AuthClientStore.setAccessToken(data.access);
          console.log("Access token refreshed successfully");

          // Verify token was actually saved
          const savedToken = AuthClientStore.getAccessToken();
          if (savedToken && savedToken === data.access) {
            return true;
          } else {
            console.error("Token was not saved correctly to storage");
            return false;
          }
        }
      } else {
        console.error("Token refresh response not OK:", response.status, response.statusText);
      }
    } catch (error) {
      console.error("Token refresh failed with exception:", error);
    }

    return false;
  }

  /**
   * Handle API errors with token refresh logic
   */
  private static async handleError(
    error: unknown,
    isRetry: boolean = false
  ): Promise<ApiError> {
    console.log("AWS API Error:", error); // Debug log to understand the error structure

    // Try to extract status code and message from various possible error structures
    let statusCode: number | undefined;
    let errorMessage: string | undefined;
    let additionalErrorData: Record<string, unknown> = {};

    // Check various possible AWS API error structures
    if (error && typeof error === "object") {
      // AWS API Gateway error structure via Amplify
      if ("response" in error) {
        const awsError = error as {
          response: {
            statusCode?: number;
            body?: { message?: string; error?: string };
          };
        };
        statusCode = awsError.response?.statusCode;
        errorMessage =
          awsError.response?.body?.message || awsError.response?.body?.error;
      }

      // Direct HTTP response error
      if ("status" in error && "message" in error) {
        const httpError = error as { status: number; message: string };
        statusCode = httpError.status;
        errorMessage = httpError.message;
      }

      // AWS Amplify RestApiError structure
      if ("name" in error && "response" in error) {
        const amplifyError = error as {
          name: string;
          response: {
            statusCode: number;
            body:
            | string
            | { success: boolean; error: string; message?: string };
          };
        };
        statusCode = amplifyError.response?.statusCode;

        // Try to parse body if it's a string
        if (typeof amplifyError.response?.body === "string") {
          try {
            const parsedBody = JSON.parse(amplifyError.response.body);
            errorMessage = parsedBody.error || parsedBody.message;
            // Preserve additional error properties like roboMarketsStatus
            const { error: _error, message: _message, success: _success, ...rest } = parsedBody;
            additionalErrorData = rest;
          } catch {
            errorMessage = amplifyError.response.body;
          }
        } else if (
          amplifyError.response?.body &&
          typeof amplifyError.response.body === "object"
        ) {
          errorMessage =
            amplifyError.response.body.error ||
            amplifyError.response.body.message;
          // Preserve additional error properties like roboMarketsStatus
          const { error: _error, message: _message, success: _success, ...rest } = amplifyError.response.body as Record<string, unknown>;
          additionalErrorData = rest;
        }
      }
    }

    // Check if this is a 401 error and we haven't retried yet
    // Trigger token refresh on ANY 401, regardless of error message wording
    if (statusCode === 401 && !isRetry) {
      console.log(
        `Detected 401 error (${errorMessage || 'Authentication failed'}), attempting token refresh...`
      );

      // Attempt to refresh the access token
      const refreshSuccess = await this.refreshAccessToken();

      if (refreshSuccess) {
        console.log("Token refresh successful, retry required");
        // Return a special error that indicates retry is needed
        return {
          message: "TOKEN_REFRESHED_RETRY_NEEDED",
          status: 401,
          statusText: "Token Refreshed - Retry Required",
        };
      } else {
        console.log("Token refresh failed, clearing tokens");
        // Refresh failed, clear tokens and return auth error
        AuthClientStore.clearTokens();
        return {
          message: "Authentication failed - please login again",
          status: 401,
          statusText: "Unauthorized",
        };
      }
    }

    // Check if this is a 503 Service Unavailable (DynamoDB throttling)
    if (statusCode === 503) {
      return {
        message: errorMessage || "Service is currently overwhelmed. Please try again later.",
        status: 503,
        statusText: "Service Unavailable",
        isServiceOverloaded: true,
      };
    }

    // Handle other API errors
    if (statusCode && (errorMessage || additionalErrorData.data)) {
      return {
        message: errorMessage || "API error occurred",
        status: statusCode,
        statusText: "API Error",
        ...additionalErrorData,
      };
    }

    // Handle generic errors
    if (error instanceof Error) {
      return {
        message: error.message,
        status: 500,
        statusText: "Internal Server Error",
      };
    }

    return {
      message: "Unknown error occurred",
      status: 500,
      statusText: "Internal Server Error",
    };
  }

  /**
   * GET request with automatic token refresh
   */
  static async apiGet<T>(path: string, config?: ApiRequestConfig): Promise<T> {
    return this.makeApiRequest<T>("GET", path, config);
  }

  /**
   * POST request with automatic token refresh
   */
  static async apiPost<T>(path: string, config?: ApiRequestConfig): Promise<T> {
    return this.makeApiRequest<T>("POST", path, config);
  }

  /**
   * PATCH request with automatic token refresh
   */
  static async apiPatch<T>(
    path: string,
    config?: ApiRequestConfig
  ): Promise<T> {
    return this.makeApiRequest<T>("PATCH", path, config);
  }

  /**
   * PUT request with automatic token refresh
   */
  static async apiPut<T>(path: string, config?: ApiRequestConfig): Promise<T> {
    return this.makeApiRequest<T>("PUT", path, config);
  }

  /**
   * DELETE request with automatic token refresh
   */
  static async apiDelete<T>(
    path: string,
    config?: ApiRequestConfig
  ): Promise<T> {
    return this.makeApiRequest<T>("DELETE", path, config);
  }

  /**
   * Make API request with automatic retry on 401 with token refresh
   */
  private static async makeApiRequest<T>(
    method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
    path: string,
    config?: ApiRequestConfig,
    isRetry: boolean = false
  ): Promise<T> {
    try {
      let restOperation;

      // Add cache-busting query parameter to prevent 304 responses
      const queryParams = {
        ...config?.queryStringParameters,
        _t: Date.now().toString(), // Add timestamp to prevent caching
      } as Record<string, string>;

      const requestOptions = {
        headers: this.mergeHeaders(config?.headers),
        queryParams,
        body: config?.body as FormData | string | undefined,
      };

      switch (method) {
        case "GET":
          restOperation = get({
            apiName: this.API_NAME,
            path: this.buildPath(path),
            options: {
              headers: requestOptions.headers,
              queryParams: requestOptions.queryParams,
            },
          });
          break;

        case "POST":
          restOperation = post({
            apiName: this.API_NAME,
            path: this.buildPath(path),
            options: {
              headers: requestOptions.headers,
              queryParams: requestOptions.queryParams,
              ...(requestOptions.body && { body: requestOptions.body }),
            },
          });
          break;

        case "PATCH":
          restOperation = patch({
            apiName: this.API_NAME,
            path: this.buildPath(path),
            options: {
              headers: requestOptions.headers,
              queryParams: requestOptions.queryParams,
              ...(requestOptions.body && { body: requestOptions.body }),
            },
          });
          break;

        case "PUT":
          restOperation = put({
            apiName: this.API_NAME,
            path: this.buildPath(path),
            options: {
              headers: requestOptions.headers,
              queryParams: requestOptions.queryParams,
              body: config?.body ? JSON.stringify(config.body) : undefined,
            },
          });
          break;

        case "DELETE":
          restOperation = del({
            apiName: this.API_NAME,
            path: this.buildPath(path),
            options: {
              headers: requestOptions.headers,
              queryParams: requestOptions.queryParams,
            },
          });
          break;

        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }

      const response = await restOperation.response;

      // Handle different response formats based on method
      if (method === "GET" || method === "POST" || method === "PATCH") {
        const contentType = (response.headers?.["content-type"] || "").split(
          ";"
        )[0];

        if (contentType === "text/csv") {
          const csvData = await response.body.text();
          return csvData as unknown as T;
        }

        const data = (await response.body.json()) as {
          success: boolean;
          data: T;
        };
        return data.data;
      } else {
        const data = await response.body.json();
        return data as T;
      }
    } catch (error: unknown) {
      const apiError = await this.handleError(error, isRetry);

      // If token was refreshed and retry is needed, try once more
      if (apiError.message === "TOKEN_REFRESHED_RETRY_NEEDED" && !isRetry) {
        console.log(`Retrying ${method} request to ${path} after token refresh...`);
        // Add a small delay to ensure token is fully propagated
        await new Promise(resolve => setTimeout(resolve, 100));
        return this.makeApiRequest<T>(method, path, config, true);
      }

      console.error(`${method} request to ${path} failed:`, apiError);
      throw apiError;
    }
  }
}
